import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Search, SortAsc, ChevronRight, Loader2, Trash2, Check, X, ListChecks, FileUp } from "lucide-react";
import { Input } from "@/components/ui/input";
import { AudioButton } from "@/components/AudioButton";
import { PageTip } from "@/components/PageTip";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { getWords, getCategories, deleteWord, deleteCategory } from "@/hooks/useFirestore";
import { ExportDialog } from "@/components/ExportDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { Word } from "@/types";
import { calculateStrength, cn } from "@/lib/utils";

type ViewTab = "all" | "category" | "wordType";

type SortOption = "firstAddedAt" | "lastReviewedAt" | "strength";

const SORT_LABELS: Record<SortOption, string> = {
  firstAddedAt: "Date Added",
  lastReviewedAt: "Last Reviewed",
  strength: "Strength",
};

export function WordBankPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<SortOption>("firstAddedAt");
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedWordIds, setSelectedWordIds] = useState<Set<string>>(new Set());
  const [viewTab, setViewTab] = useState<ViewTab>("all");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedWordType, setSelectedWordType] = useState<string | null>(null);
  const [managingCategories, setManagingCategories] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportScope, setExportScope] = useState<"all" | "selection">("all");

  const { data: words, isLoading, error } = useQuery({
    queryKey: ["words", sortBy, user?.uid],
    queryFn: () => getWords(user!.uid, {
      orderByField: sortBy === "strength" ? "firstAddedAt" : sortBy,
    }),
    enabled: !!user,
  });

  const { data: categories } = useQuery({
    queryKey: ["categories", user?.uid],
    queryFn: () => getCategories(user!.uid),
    enabled: !!user,
  });

  // Delete selected words mutation
  const deleteSelectedMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Not authenticated");
      const wordIds = Array.from(selectedWordIds);
      for (const wordId of wordIds) {
        await deleteWord(wordId, user.uid);
      }
      return wordIds.length;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ["words"] });
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      queryClient.invalidateQueries({ queryKey: ["studySets"] });
      setSelectedWordIds(new Set());
      setIsSelectMode(false);
      console.log(`Deleted ${count} words`);
    },
  });

  const toggleWordSelection = (wordId: string) => {
    setSelectedWordIds((prev) => {
      const next = new Set(prev);
      if (next.has(wordId)) {
        next.delete(wordId);
      } else {
        next.add(wordId);
      }
      return next;
    });
  };

  const selectAllWords = () => {
    if (filteredWords) {
      setSelectedWordIds(new Set(filteredWords.map((w) => w.id)));
    }
  };

  const exitSelectMode = () => {
    setIsSelectMode(false);
    setSelectedWordIds(new Set());
  };

  // Filter words by search query
  const filteredWords = useMemo(() => {
    let result = words?.filter((word) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        word.hebrew.text.includes(searchQuery) ||
        word.hebrew.normalizedText.includes(searchQuery) ||
        Object.values(word.translations).some((t) =>
          t?.text.toLowerCase().includes(query)
        ) ||
        word.categories.some((c) => c.toLowerCase().includes(query))
      );
    });
    // Client-side sort by strength (weakest first)
    if (sortBy === "strength" && result) {
      const order = { weak: 0, learning: 1, strong: 2 };
      result = [...result].sort((a, b) => {
        const aS = calculateStrength(a.reviewStats.knownCount, a.reviewStats.unknownCount);
        const bS = calculateStrength(b.reviewStats.knownCount, b.reviewStats.unknownCount);
        return order[aS] - order[bS];
      });
    }
    return result;
  }, [words, searchQuery, sortBy]);

  const cycleSortBy = () => {
    const options: SortOption[] = ["firstAddedAt", "lastReviewedAt", "strength"];
    const currentIndex = options.indexOf(sortBy);
    const nextIndex = (currentIndex + 1) % options.length;
    setSortBy(options[nextIndex]);
  };

  // Filter by category or word type
  const displayWords = useMemo(() => {
    if (viewTab === "category" && selectedCategory) {
      return filteredWords?.filter((w) => w.categories.includes(selectedCategory));
    }
    if (viewTab === "wordType" && selectedWordType) {
      return filteredWords?.filter((w) => w.wordType === selectedWordType);
    }
    return filteredWords;
  }, [filteredWords, viewTab, selectedCategory, selectedWordType]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Failed to load words. Please try again.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">My Words</h1>
        <p className="text-muted-foreground">
          {words?.length || 0} words saved
        </p>
      </div>
      <PageTip pageKey="my-words" message="Your personal word bank. Tap any word to see its grammar, conjugation, and example sentences. Filter by category or sort by strength to find what you need." />

      {/* View Tabs */}
      <div className="flex gap-2">
        {(["all", "category", "wordType"] as ViewTab[]).map((tab) => (
          <Button
            key={tab}
            variant={viewTab === tab ? "default" : "outline"}
            size="sm"
            onClick={() => { setViewTab(tab); setSelectedCategory(null); setSelectedWordType(null); }}
          >
            {tab === "all" ? "All" : tab === "category" ? "By Category" : "By Word Type"}
          </Button>
        ))}
      </div>

      {/* Category selector when in category view */}
      {viewTab === "category" && categories && (
        <div className="flex flex-wrap gap-2 items-center">
          {categories.map((cat) => (
            <div key={cat.id} className="flex items-center gap-0.5">
              <Button
                variant={selectedCategory === cat.name ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(cat.name)}
              >
                {cat.name} ({cat.wordCount})
              </Button>
              {managingCategories && (
                <button
                  className="p-1 rounded-full hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm(`Delete category "${cat.name}"? Words won't be deleted.`)) {
                      deleteCategory(user!.uid, cat.id, cat.name).then(() => {
                        queryClient.invalidateQueries({ queryKey: ["categories"] });
                        queryClient.invalidateQueries({ queryKey: ["words"] });
                        if (selectedCategory === cat.name) setSelectedCategory(null);
                      });
                    }
                  }}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setManagingCategories(!managingCategories)}
            className="text-xs text-muted-foreground"
          >
            {managingCategories ? "Done" : "Manage"}
          </Button>
        </div>
      )}

      {/* Word Type selector */}
      {viewTab === "wordType" && (
        <div className="flex flex-wrap gap-2">
          {([
            { key: "verb", label: "Verbs (פועל)" },
            { key: "noun", label: "Nouns (שם עצם)" },
            { key: "adjective", label: "Adjectives (תואר)" },
            { key: "adverb", label: "Adverbs (תה״פ)" },
            { key: "other", label: "Other" },
          ] as const).map(({ key, label }) => {
            const count = words?.filter((w) => w.wordType === key).length || 0;
            return (
              <Button
                key={key}
                variant={selectedWordType === key ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedWordType(key)}
              >
                {label} ({count})
              </Button>
            );
          })}
        </div>
      )}

      {/* Search and Sort */}
      <div className="flex gap-2" data-walkthrough="wordbank-search">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search words..."
            className="pl-9"
          />
        </div>
        {!isSelectMode ? (
          <>
            <Button variant="outline" onClick={cycleSortBy} className="gap-2">
              <SortAsc className="h-4 w-4" />
              {SORT_LABELS[sortBy]}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  disabled={!words || words.length === 0}
                  className="gap-2"
                >
                  <FileUp className="h-4 w-4" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => {
                    setExportScope("all");
                    setShowExportDialog(true);
                  }}
                >
                  <FileUp className="h-4 w-4 mr-2" />
                  All words ({words?.length || 0})
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setIsSelectMode(true)}>
                  <ListChecks className="h-4 w-4 mr-2" />
                  Choose specific words…
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              onClick={() => setIsSelectMode(true)}
              className="gap-2"
            >
              <ListChecks className="h-4 w-4" />
              Select
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={selectAllWords} size="sm">
              Select All
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setExportScope("selection");
                setShowExportDialog(true);
              }}
              disabled={selectedWordIds.size === 0}
              size="sm"
            >
              <FileUp className="h-4 w-4 mr-1" />
              Export ({selectedWordIds.size})
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteSelectedMutation.mutate()}
              disabled={selectedWordIds.size === 0 || deleteSelectedMutation.isPending}
              size="sm"
            >
              {deleteSelectedMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <Trash2 className="h-4 w-4 mr-1" />
              )}
              Delete ({selectedWordIds.size})
            </Button>
            <Button variant="ghost" onClick={exitSelectMode} size="sm">
              <X className="h-4 w-4" />
            </Button>
          </>
        )}
      </div>

      {/* Word List */}
      <div className="space-y-2">
        {(displayWords?.length === 0) ? (
          <div className="text-center py-12 text-muted-foreground">
            {searchQuery ? "No words match your search" :
             viewTab === "category" && selectedCategory ? `No words in "${selectedCategory}"` :
             viewTab === "category" ? "Select a category above" :
             viewTab === "wordType" && selectedWordType ? `No ${selectedWordType}s found` :
             viewTab === "wordType" ? "Select a word type above" :
             "No words yet. Start adding words!"}
          </div>
        ) : (
          displayWords?.map((word, index) => (
            <WordCard
              key={word.id}
              word={word}
              isSelectMode={isSelectMode}
              isSelected={selectedWordIds.has(word.id)}
              onToggleSelect={() => toggleWordSelection(word.id)}
              walkthroughId={index === 0 ? "wordbank-card" : undefined}
            />
          ))
        )}
      </div>

      {/* Export: everything, or just the words picked in select mode */}
      <ExportDialog
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        words={
          exportScope === "selection"
            ? (words || []).filter((w) => selectedWordIds.has(w.id))
            : words || []
        }
        name="my-words"
      />

    </div>
  );
}

function WordCard({
  word,
  isSelectMode,
  isSelected,
  onToggleSelect,
  walkthroughId,
}: {
  word: Word;
  isSelectMode: boolean;
  isSelected: boolean;
  onToggleSelect: () => void;
  walkthroughId?: string;
}) {
  const strength = calculateStrength(
    word.reviewStats.knownCount,
    word.reviewStats.unknownCount
  );

  const strengthColors = {
    weak: "bg-red-100 text-red-700",
    learning: "bg-amber-100 text-amber-700",
    strong: "bg-green-100 text-green-700",
  };

  // Get primary translation (prefer English, then Russian)
  const primaryTranslation =
    word.translations.en?.text || word.translations.ru?.text || "";

  // Get grammar badge text
  const getGrammarBadge = () => {
    if (word.wordType === "noun" && word.nounInfo?.gender) {
      return word.nounInfo.gender === "masculine" ? "ז׳" :
             word.nounInfo.gender === "feminine" ? "נ׳" : "ז׳/נ׳";
    }
    if (word.wordType === "verb" && word.verbInfo?.binyan) {
      return word.verbInfo.binyan;
    }
    return null;
  };

  const grammarBadge = getGrammarBadge();

  const cardContent = (
    <Card
      className={cn(
        "p-4 cursor-pointer",
        isSelectMode ? (isSelected ? "bg-primary/10 border-primary" : "hover:bg-muted/50") : "card-hover"
      )}
      onClick={isSelectMode ? onToggleSelect : undefined}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            {/* Checkbox or Strength Indicator */}
            {isSelectMode ? (
              <div
                className={cn(
                  "w-5 h-5 rounded border flex items-center justify-center flex-shrink-0",
                  isSelected
                    ? "bg-primary border-primary text-primary-foreground"
                    : "border-muted-foreground"
                )}
              >
                {isSelected && <Check className="h-3 w-3" />}
              </div>
            ) : (
              <div
                className={cn(
                  "w-2 h-2 rounded-full flex-shrink-0",
                  strength === "weak" && "bg-red-500",
                  strength === "learning" && "bg-amber-500",
                  strength === "strong" && "bg-green-500"
                )}
              />
            )}

            {/* Hebrew Text */}
            <span className="text-lg font-medium hebrew-text flex-shrink-0" dir="rtl">
              {word.hebrew.text}
            </span>
            {!isSelectMode && <AudioButton text={word.hebrew.text} size="sm" />}

            {/* Transliteration */}
            {word.hebrew.transliteration && (
              <span className="text-sm text-muted-foreground italic">
                ({word.hebrew.transliteration})
              </span>
            )}

            {/* Grammar Badge */}
            {grammarBadge && (
              <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                {grammarBadge}
              </span>
            )}

            {/* Translation */}
            <span className="text-muted-foreground truncate">
              {primaryTranslation}
            </span>
          </div>

          {/* Categories */}
          {word.categories.length > 0 && (
            <div className="flex gap-1 mt-2 ml-5">
              {word.categories.slice(0, 3).map((cat) => (
                <Badge key={cat} variant="secondary" className="text-xs">
                  {cat}
                </Badge>
              ))}
              {word.categories.length > 3 && (
                <Badge variant="secondary" className="text-xs">
                  +{word.categories.length - 3}
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* Stats */}
        {!isSelectMode && (
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            {word.lookupCount > 1 && (
              <span className="text-xs">
                {word.lookupCount}×
              </span>
            )}
            <div className={cn("px-2 py-1 rounded text-xs", strengthColors[strength])}>
              {word.reviewStats.knownCount}/{word.reviewStats.knownCount + word.reviewStats.unknownCount}
            </div>
            <ChevronRight className="h-4 w-4" />
          </div>
        )}
      </div>
    </Card>
  );

  if (isSelectMode) {
    return cardContent;
  }

  return <Link to={`/words/${word.id}`} data-walkthrough={walkthroughId}>{cardContent}</Link>;
}
